import { ReactElement } from 'react';

// Column definitions for the print data table
export interface PrintColumn {
  displayName: string;
  internalName: string;
  source: 'run' | 'average' | 'config';
  places?: number;
  dividerAfter?: boolean;
  invert?: boolean; // Multiply value by -1 before display
}

export interface PrintDataRow {
  type: 'config' | 'run';
  config: any;
  run?: any;
}

export function formatTimeValue(value: string | null | undefined): string {
  if (!value) return '-';
  try {
    const date = new Date(value);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return String(value);
  }
}

export function formatCellValue(
  value: any, places: number | undefined, internalName: string, invert?: boolean,
): string {
  // Special handling for timestamp
  if (internalName === 'run_ts_tz') {
    return formatTimeValue(value);
  }

  // Handle string values
  if (places === -1) {
    return value != null ? String(value) : '-';
  }

  // Handle numeric values
  if (typeof value === 'number') {
    const displayValue = invert ? value * -1 : value;
    return displayValue.toFixed(places ?? 2);
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'number') {
    const displayValue = invert ? value[0] * -1 : value[0];
    return displayValue.toFixed(places ?? 2);
  }
  if (value != null) {
    return String(value);
  }
  return '-';
}

export function PrintDataTablePage({
  columns,
  rows,
  title,
  getCellValue,
}: {
  columns: PrintColumn[];
  rows: PrintDataRow[];
  title: string;
  getCellValue: (row: PrintDataRow, column: PrintColumn) => any;
}): ReactElement {
  return (
    <div className="print-data-table-page">
      <div className="print-section-header">{title}</div>
      <table className="print-data-table-content">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.internalName}
                className={col.dividerAfter ? 'print-divider-right' : ''}
                dangerouslySetInnerHTML={{ __html: col.displayName }}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            const isConfig = row.type === 'config';
            const rowClass = isConfig ? 'print-config-row' : 'print-run-row';
            const key = isConfig ? `config-${row.config.id}-${rowIdx}` : `run-${row.run?.id || rowIdx}`;

            return (
              <tr key={key} className={rowClass}>
                {columns.map((col) => {
                  const value = getCellValue(row, col);
                  const formatted = formatCellValue(value, col.places, col.internalName, col.invert);
                  return (
                    <td
                      key={col.internalName}
                      className={col.dividerAfter ? 'print-divider-right' : ''}
                    >
                      {formatted}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Build rows array from configs for the print data table
 */
export function buildPrintDataRows(configs: any[]): PrintDataRow[] {
  const rows: PrintDataRow[] = [];

  for (const config of configs) {
    // Add config row
    rows.push({ type: 'config', config });
    // Add run rows
    if (config.runs) {
      for (const run of config.runs) {
        rows.push({ type: 'run', config, run });
      }
    }
  }

  return rows;
}

/**
 * Standard getCellValue function for print data tables
 */
export function getStandardCellValue(row: PrintDataRow, column: PrintColumn): any {
  if (row.type === 'config') {
    // For config rows, show config-level averages or metadata
    if (column.source === 'config') {
      return row.config[column.internalName];
    }
    if (column.source === 'average') {
      return row.config.average?.[column.internalName];
    }
    if (column.internalName === 'run_number') {
      return row.config.letter || '';
    }
    if (column.internalName === 'description') {
      return row.config.description || '';
    }
    return null;
  }

  // For run rows
  if (column.source === 'run') {
    return row.run?.[column.internalName];
  }
  if (column.source === 'average') {
    return row.run?.average?.[column.internalName];
  }
  if (column.source === 'config') {
    // Config values only shown on config rows, leave run rows blank
    return null;
  }
  return null;
}

/**
 * Reusable PrintDataTable component that renders two pages of data
 */
export function PrintDataTable({
  rows,
  columnsPage1,
  columnsPage2,
  titlePage1,
  titlePage2,
  getCellValue = getStandardCellValue,
  className = 'print-only print-data-table',
}: {
  rows: PrintDataRow[];
  columnsPage1: PrintColumn[];
  columnsPage2: PrintColumn[];
  titlePage1: string;
  titlePage2: string;
  getCellValue?: (row: PrintDataRow, column: PrintColumn) => any;
  className?: string;
}): ReactElement {
  return (
    <div className={className}>
      {/* Page 1 */}
      <PrintDataTablePage
        columns={columnsPage1}
        rows={rows}
        title={titlePage1}
        getCellValue={getCellValue}
      />

      {/* Page 2 */}
      <div className="print-page-break-before">
        <PrintDataTablePage
          columns={columnsPage2}
          rows={rows}
          title={titlePage2}
          getCellValue={getCellValue}
        />
      </div>
    </div>
  );
}
